import Keycloak, { KeycloakTokenParsed } from 'keycloak-js';
import { KeycloakError } from 'src/utils/errors/KeycloakError';
import { create } from 'zustand';
import { getRuntimeConfig } from 'src/utils/runtimeConfig';

/**
 * Safely convert configuration value to string
 * @param value - The configuration value to convert
 * @param defaultValue - Default value to use if conversion fails
 * @returns String representation of the value or default value
 */
function configValueToString(value: unknown, defaultValue: string): string {
  if (typeof value === 'string') {
    return value;
  }
  if (value === null || value === undefined) {
    return defaultValue;
  }
  // For non-string, non-null values, return default instead of converting to string
  return defaultValue;
}

interface KeycloakState {
  keycloak?: Keycloak;
  initialized: boolean;
  authenticated: boolean;
  idTokenParsed: KeycloakTokenParsed | undefined;
  refreshTokenParsed: KeycloakTokenParsed | undefined;
  tokenMinValiditySeconds: number;
  actions: {
    initialize: () => Promise<void>;
    login: () => Promise<void>;
    logout: () => Promise<void>;
    updateToken: (forceRefresh?: boolean) => Promise<boolean>;
    getToken: () => string | undefined;
  };
}

export const getTokenMinValidity = (keycloak: Keycloak) => {
  const exp = keycloak.idTokenParsed?.exp;
  const iat = keycloak.idTokenParsed?.iat;
  if (!exp || !iat) {
    return 5; // Default to 5 seconds if exp or iat is not available
  }
  const diff = exp - iat;
  // Calculate the minimum validity as 30% of the total validity
  const minValidity = Math.floor(diff * 0.3);
  return minValidity;
};

export const useKeycloak = create<KeycloakState>((set, get) => ({
  keycloak: undefined,
  initialized: false,
  authenticated: false,
  idTokenParsed: undefined,
  refreshTokenParsed: undefined,
  tokenMinValiditySeconds: 5,
  actions: {
    initialize: async () => {
      if (get().initialized) return;

      // Create Keycloak instance only after config is loaded
      const config = getRuntimeConfig();
      const keycloak = new Keycloak({
        url: configValueToString(config.VITE_KEYCLOAK_URL, ''),
        realm: configValueToString(config.VITE_KEYCLOAK_REALM, ''),
        clientId: configValueToString(config.VITE_KEYCLOAK_CLIENT_ID, ''),
      });

      try {
        await keycloak.init({
          onLoad: 'check-sso',
          silentCheckSsoRedirectUri: `${location.origin}/silent-check-sso.html`,
        });

        keycloak.onAuthRefreshError = () => {
          get().actions.logout();
        };

        set({
          keycloak,
          initialized: keycloak.didInitialize,
          authenticated: keycloak.authenticated,
          idTokenParsed: keycloak.idTokenParsed,
          refreshTokenParsed: keycloak.refreshTokenParsed,
          tokenMinValiditySeconds: getTokenMinValidity(keycloak),
        });
      } catch (err) {
        throw new KeycloakError((err as Error).message);
      }
    },
    login: async () => {
      const keycloak = get().keycloak;
      if (!keycloak) throw new Error('Keycloak not initialized');
      return keycloak.login();
    },
    logout: async () => {
      const keycloak = get().keycloak;
      if (!keycloak) throw new Error('Keycloak not initialized');
      return keycloak.logout();
    },
    updateToken: async (forceRefresh?: boolean) => {
      const keycloak = get().keycloak;
      if (!keycloak) throw new Error('Keycloak not initialized');
      const updated = await keycloak.updateToken(
        forceRefresh ? -1 : get().tokenMinValiditySeconds,
      );
      if (updated) {
        set({
          idTokenParsed: keycloak.idTokenParsed,
          refreshTokenParsed: keycloak.refreshTokenParsed,
          tokenMinValiditySeconds: getTokenMinValidity(keycloak),
        });
      }
      return updated;
    },
    getToken: () => {
      const keycloak = get().keycloak;
      return keycloak?.token;
    },
  },
}));
